import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import Editor from '@monaco-editor/react';
import clsx from 'clsx';
import { api } from '../../utils/api';

const LANGUAGE_MAP = {
    js: 'javascript',
    jsx: 'javascript',
    ts: 'typescript',
    tsx: 'typescript',
    py: 'python',
    json: 'json',
    html: 'html',
    css: 'css',
    md: 'markdown',
    txt: 'plaintext',
};

function inferLanguage(path) {
    if (!path) return 'plaintext';
    const parts = path.split('.');
    if (parts.length < 2) return 'plaintext';
    const ext = parts.pop()?.toLowerCase();
    return LANGUAGE_MAP[ext] || 'plaintext';
}

const defaultStatus = { loading: false, saving: false, dirty: false };

const MonacoFileEditor = forwardRef(function MonacoFileEditor(
    {
        projectId,
        filePath,
        className,
        fallbackContent,
        onStateChange,
        onError,
    },
    ref,
) {
    const [content, setContent] = useState('');
    const [language, setLanguage] = useState('plaintext');
    const [status, setStatus] = useState(defaultStatus);
    const [loadError, setLoadError] = useState(null);

    useEffect(() => {
        onStateChange?.(status);
    }, [status, onStateChange]);

    useEffect(() => {
        if (!filePath) {
            setContent('');
            setLanguage('plaintext');
            setStatus({ ...defaultStatus });
            setLoadError(null);
            return;
        }

        let cancelled = false;
        const fetchFile = async () => {
            setContent(fallbackContent ?? '');
            setLanguage(inferLanguage(filePath));
            setStatus({ loading: true, saving: false, dirty: false });
            setLoadError(null);
            try {
                const { data } = await api.get(`/files/${projectId}`, {
                    params: { path: filePath },
                });
                if (cancelled) return;
                setContent(data?.content ?? '');
                setLanguage(data?.language || inferLanguage(filePath));
                setStatus({ loading: false, saving: false, dirty: false });
            } catch (error) {
                if (cancelled) return;
                setStatus({ loading: false, saving: false, dirty: false });
                setLoadError(error);
                setContent(fallbackContent ?? '');
                onError?.(error, { phase: 'load', filePath });
            }
        };

        fetchFile();

        return () => {
            cancelled = true;
        };
    }, [fallbackContent, filePath, projectId, onError]);

    const saveFile = async () => {
        if (!filePath) return null;
        setStatus((prev) => ({ ...prev, saving: true }));
        try {
            const payload = {
                path: filePath,
                content,
                language,
            };
            const { data } = await api.put(`/files/${projectId}`, payload);
            setContent(data?.content ?? content);
            if (data?.language) {
                setLanguage(data.language);
            }
            setStatus({ loading: false, saving: false, dirty: false });
            return data;
        } catch (error) {
            setStatus((prev) => ({ ...prev, saving: false }));
            onError?.(error, { phase: 'save', filePath, content });
            throw error;
        }
    };

    const acknowledgeSave = (nextContent, nextLanguage) => {
        if (typeof nextContent === 'string') {
            setContent(nextContent);
        }
        if (nextLanguage) {
            setLanguage(nextLanguage);
        }
        setStatus((prev) => ({ ...prev, dirty: false }));
    };

    useImperativeHandle(
        ref,
        () => ({
            saveFile,
            getContent: () => content,
            getLanguage: () => language,
            acknowledgeSave,
        }),
        [acknowledgeSave, content, language, saveFile],
    );

    const handleEditorChange = (value) => {
        setContent(value ?? '');
        setStatus((prev) => ({ ...prev, dirty: true }));
    };

    return (
        <div className={clsx('relative h-full w-full', className)}>
            {!filePath ? (
                <div className="flex h-full items-center justify-center text-slate-400">
                    Select a file to edit
                </div>
            ) : (
                <Editor
                    key={filePath}
                    height="100%"
                    language={language || inferLanguage(filePath)}
                    value={content}
                    onChange={handleEditorChange}
                    theme="vs-dark"
                    options={{
                        minimap: { enabled: false },
                        fontSize: 14,
                        lineNumbers: 'on',
                        scrollBeyondLastLine: false,
                        automaticLayout: true,
                    }}
                />
            )}
            {status.loading && (
                <div className="absolute inset-0 flex items-center justify-center bg-slate-900/70 text-sm text-slate-200">
                    Loading file...
                </div>
            )}
            {loadError && !status.loading && filePath && (
                <div className="absolute inset-0 flex items-center justify-center bg-red-900/60 text-sm text-red-100">
                    Failed to load file
                </div>
            )}
        </div>
    );
});

export default MonacoFileEditor;
